import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { RotateCcw, Trash2, Trash as TrashIcon, Folder, File } from 'lucide-react';
import { api } from '../lib/api';
import { formatBytes, formatDate } from '../lib/utils';
import { Lamp } from '../components/aceternity/Lamp';
import type { Node } from '@hongweibing-netdisk/shared';
import toast from 'react-hot-toast';

interface TrashRow {
  id: string;
  name: string;
  mime_type: string;
  size_bytes: number;
  deleted_at: number;
}

export function Trash() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['trash'],
    queryFn: () => api.trash.list(1),
  });
  const items: TrashRow[] = (data?.items ?? []).map((r: any) => ({
    id: r.id, name: r.name, mime_type: r.mime_type, size_bytes: r.size_bytes, deleted_at: r.deleted_at,
  }));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['trash'] });
    qc.invalidateQueries({ queryKey: ['nodes'] });
  };

  const restore = async (id: string) => {
    await api.trash.restore(id);
    toast.success('已还原');
    invalidate();
  };

  const purge = async (id: string) => {
    if (!confirm('永久删除后不可恢复，确定？')) return;
    await api.trash.purge(id);
    toast.success('已永久删除');
    invalidate();
  };

  const empty = async () => {
    if (!confirm('清空回收站会永久删除全部文件，确定？')) return;
    await api.trash.empty();
    toast.success('回收站已清空');
    setSelected([]);
    invalidate();
  };

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="h-full flex flex-col animate-fadeIn relative">
      <Lamp />
      <div className="flex items-end sm:items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.35em] text-danger/80">RECYCLE BIN</span>
            <span className="h-px w-10 bg-gradient-to-r from-danger to-transparent" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black neon-text tracking-tight mt-0.5 flex items-center gap-2">
            <TrashIcon className="w-6 h-6 text-danger" /> 回收站
          </h1>
          <p className="text-xs sm:text-sm text-text-muted mt-1">文件将在删除 30 天后自动清除</p>
        </div>
        {items.length > 0 && (
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }} onClick={empty} className="flex items-center gap-1.5 px-3 py-2 bg-danger/10 hover:bg-danger/20 text-danger border border-danger/30 rounded-xl text-sm font-semibold flex-shrink-0 transition-colors">
            <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">清空回收站</span><span className="sm:hidden">清空</span>
          </motion.button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-14" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-text-muted gap-3 animate-fadeInUp">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-card border border-border-soft animate-float">
            <TrashIcon className="w-10 h-10 opacity-50" />
          </div>
          <p>回收站是空的</p>
        </div>
      ) : (
        <div className="bg-card/60 border border-border-soft rounded-2xl divide-y divide-border-soft overflow-hidden relative">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-danger via-primary to-accent-violet opacity-50" />
          {items.map((r) => (
            <div key={r.id}
              onClick={() => toggle(r.id)}
              className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 cursor-pointer transition-colors ${selected.includes(r.id) ? 'bg-primary/10' : 'hover:bg-bg-secondary/60'}`}>
              {r.mime_type === 'application/folder'
                ? <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-accent-amber/12 flex-shrink-0"><Folder className="w-5 h-5 text-accent-amber" /></span>
                : <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-accent-cyan/12 flex-shrink-0"><File className="w-5 h-5 text-accent-cyan" /></span>}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text truncate">{r.name}</p>
                <p className="text-xs text-text-muted truncate">删除于 {formatDate(r.deleted_at)}</p>
              </div>
              <span className="text-xs text-text-muted flex-shrink-0 hidden sm:inline">{r.mime_type === 'application/folder' ? '—' : formatBytes(r.size_bytes)}</span>
              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => restore(r.id)} title="还原" className="p-2 rounded-xl hover:bg-accent-cyan/20 text-text-muted hover:text-accent-cyan transition-colors btn-press">
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button onClick={() => purge(r.id)} title="永久删除" className="p-2 rounded-xl hover:bg-danger/20 text-text-muted hover:text-danger transition-colors btn-press">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
